import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import * as EmptySectionContent from 'src/components/common/EmptySectionContent';
import { Content } from 'src/contexts/ContentContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { useReachProConversationEvents } from 'src/hooks/api/useReachProConversationEvents';
import { useReachProConversations } from 'src/hooks/api/useReachProConversations';
import { useReachProMessages } from 'src/hooks/api/useReachProMessages';
import { useReachProSendMessage } from 'src/hooks/api/useReachProSendMessage';
import { ContentId } from 'src/utils/constants/contentId';
import { stringToUtcDate } from 'src/utils/dateTimeUtils';

import IconBulletList from '~icons/pos/bulletList';

import { ReachProSupportChatInput } from './ReachProSupportChatInput';
import * as styles from './ReachProSupportChatPanel.css';
import { ReachProSupportMessage } from './ReachProSupportMessage';

export interface Message {
  id: string;
  content: string;
  senderName: string;
  timestamp: Date;
  isSent: boolean;
}

export function ReachProSupportChatPanel() {
  const { reachProConversationId } = useParams();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { conversations } = useReachProConversations();
  const { catalogEventsLookup } = useReachProConversationEvents(conversations);
  const { data: messages, isLoading } = useReachProMessages(
    reachProConversationId
  );

  const sendMessage = useReachProSendMessage(reachProConversationId || '');

  const conversation = useMemo(() => {
    if (!conversations || !reachProConversationId) return null;
    return conversations.find(
      (c) => c.conversationId.toString() === reachProConversationId
    );
  }, [conversations, reachProConversationId]);

  const eventInfo = useMemo(() => {
    if (!conversation?.eventId || !catalogEventsLookup) return null;
    return catalogEventsLookup[conversation.eventId.toString()] ?? null;
  }, [conversation?.eventId, catalogEventsLookup]);

  const venueLocation = useMemo(() => {
    if (!eventInfo) return null;
    return [eventInfo.venueCity, eventInfo.venueState, eventInfo.venueCountry]
      .filter(Boolean)
      .join(', ');
  }, [eventInfo]);

  const seatingDisplay = useMemo(() => {
    if (!conversation) return null;
    const parts: string[] = [];
    if (conversation.section) parts.push(conversation.section);
    if (conversation.row) parts.push(`Row ${conversation.row}`);
    if (conversation.seatFrom || conversation.seatTo) {
      const seats =
        conversation.seatFrom && conversation.seatTo
          ? `${conversation.seatFrom}-${conversation.seatTo}`
          : conversation.seatFrom || conversation.seatTo;
      parts.push(`Seats ${seats}`);
    }
    return parts.length > 0 ? parts.join(' • ') : null;
  }, [conversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = (content: string) => {
    sendMessage.mutate(content);
  };

  if (!reachProConversationId) {
    return (
      <div className={styles.emptyState}>
        <EmptySectionContent.Root icon={<IconBulletList />}>
          <Content id={ContentId.NoConversation} />
        </EmptySectionContent.Root>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.emptyState}>
        <PosSpinner />
      </div>
    );
  }

  const hasIdsOrSeating =
    conversation?.eventId ||
    conversation?.stubHubListingId ||
    conversation?.stubHubSaleId ||
    seatingDisplay;

  return (
    <div className={styles.chatPanel}>
      {conversation && (
        <div className={styles.conversationHeader}>
          {/* Line 1: Subject */}
          <div className={styles.headerSubject}>
            {conversation.topic || (
              <Content id={ContentId.SupportConversation} />
            )}
          </div>

          {/* Line 2: Artist • Venue • Date */}
          {eventInfo && (
            <div className={styles.headerEventInfo}>
              <span className={styles.headerValue}>
                {eventInfo.performerName || eventInfo.eventName}
              </span>
              {eventInfo.venueName && (
                <span className={styles.headerLabel}>
                  {' • '}
                  {eventInfo.venueName}
                  {venueLocation && ` (${venueLocation})`}
                </span>
              )}
              {eventInfo.eventStartDate && (
                <span className={styles.headerLabel}>
                  {' • '}
                  {new Date(eventInfo.eventStartDate).toLocaleDateString([], {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              )}
            </div>
          )}

          {/* Line 3: Event ID, Sale/Listing ID, Seating */}
          {hasIdsOrSeating && (
            <div className={styles.headerIdsRow}>
              {conversation.eventId && (
                <div className={styles.headerItem}>
                  <span className={styles.headerLabel}>
                    <Content id={ContentId.StubHubEventId} />:
                  </span>
                  <span className={styles.headerValue}>
                    {conversation.eventId}
                  </span>
                </div>
              )}
              {conversation.stubHubListingId && (
                <div className={styles.headerItem}>
                  <span className={styles.headerLabel}>
                    <Content id={ContentId.ListingId} />:
                  </span>
                  <span className={styles.headerValue}>
                    {conversation.stubHubListingId}
                  </span>
                </div>
              )}
              {conversation.stubHubSaleId && (
                <div className={styles.headerItem}>
                  <span className={styles.headerLabel}>
                    <Content id={ContentId.SaleId} />:
                  </span>
                  <span className={styles.headerValue}>
                    {conversation.stubHubSaleId}
                  </span>
                </div>
              )}
              {seatingDisplay && (
                <div className={styles.headerItem}>
                  <span className={styles.headerLabel}>
                    <Content id={ContentId.Seating} />:
                  </span>
                  <span className={styles.headerValue}>{seatingDisplay}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div className={styles.messagesContainer}>
        {messages?.map((message, index) => (
          <ReachProSupportMessage
            key={message.workItemNoteId?.toString() ?? `message-${index}`}
            message={{
              id: message.workItemNoteId?.toString() ?? `message-${index}`,
              content: message.messageContent,
              senderName: message.senderName,
              timestamp: stringToUtcDate(message.timestamp),
              isSent: message.isSent,
            }}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>
      <ReachProSupportChatInput
        onSend={handleSendMessage}
        isLoading={sendMessage.isPending}
      />
      {sendMessage.isError && (
        <div style={{ padding: vars.spacing.m, color: vars.color.textErrorV2 }}>
          Failed to send message. Please try again.
        </div>
      )}
    </div>
  );
}
